import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Plus, Trash2, Mail, Phone, FolderOpen } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import StatusPill from "../components/StatusPill";
import CreateProjectModal from "./CreateProjectModal";
import { formatDate } from "../lib/constants";

export default function AdminClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [err, setErr] = useState("");
  const [showEditClient, setShowEditClient] = useState(false);
  const [showCreateProject, setShowCreateProject] = useState(false);

  const load = useCallback(async () => {
    setErr("");
    try {
      const { data } = await api.get(`/clients/${id}/detail`);
      setDetail(data);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (!detail) {
    return (
      <div className="max-w-6xl mx-auto px-10 py-16">
        <div className="label">{err ? err : "Loading…"}</div>
      </div>
    );
  }

  const { client, projects, active_project_count } = detail;
  const activeProjects = projects.filter((p) => !p.is_archived);
  const archived = projects.filter((p) => p.is_archived);

  const deleteClient = async () => {
    if (!confirm(`Delete ${client.name}? You must archive/delete their projects first.`)) return;
    try {
      await api.delete(`/clients/${id}`);
      navigate("/admin/clients");
    } catch (e) { alert(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  return (
    <div className="max-w-6xl mx-auto px-8 sm:px-12 py-12 fade-up" data-testid="admin-client-detail">
      <Link to="/admin/clients" className="inline-flex items-center gap-2 text-xs text-[color:var(--atech-text-dim)] hover:text-[color:var(--atech-text)] mb-6">
        <ArrowLeft size={14} /> All clients
      </Link>

      {/* Header */}
      <div className="flex flex-col xl:flex-row xl:items-end xl:justify-between gap-6 mb-12">
        <div>
          <div className="label text-[color:var(--atech-copper)] mb-2">
            Client organisation {client.is_demo && "· Demo"}
          </div>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tight text-[color:var(--atech-text)] leading-tight">
            {client.name}
          </h1>
          <div className="mt-3 text-xs text-[color:var(--atech-text-dim)] flex flex-wrap gap-x-5 gap-y-1">
            {client.contact_name && <span>{client.contact_name}</span>}
            {client.contact_email && <span className="inline-flex items-center gap-1"><Mail size={11} /> {client.contact_email}</span>}
            {client.contact_phone && <span className="inline-flex items-center gap-1"><Phone size={11} /> {client.contact_phone}</span>}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => setShowEditClient(true)} className="text-sm px-4 py-2.5 rounded-[2px] border border-[color:var(--atech-border)] text-[color:var(--atech-text-dim)] hover:text-[color:var(--atech-text)] hover:border-[color:var(--atech-border-strong)]" data-testid="edit-client-button">
            Edit details
          </button>
          <button onClick={deleteClient} className="text-sm px-4 py-2.5 rounded-[2px] border border-[rgba(220,38,38,0.3)] text-[#F87171] hover:bg-[rgba(220,38,38,0.08)] flex items-center gap-2" data-testid="delete-client-detail-button">
            <Trash2 size={14} /> Delete client
          </button>
        </div>
      </div>

      {/* Info note: users are now managed at project level */}
      <div className="mb-10 p-4 border border-[color:var(--atech-border)] bg-[color:var(--atech-surface-elevated)]/40 rounded-[2px] text-xs text-[color:var(--atech-text-dim)] leading-relaxed" data-testid="client-detail-users-info">
        Portal users are managed at the <span className="text-[color:var(--atech-copper)]">project level</span>.
        Open a project to add or invite users, or visit{" "}
        <Link to="/admin/settings?tab=portal_users" className="text-[color:var(--atech-copper)] hover:underline">
          Settings → Portal users
        </Link>{" "}
        to manage everyone in one place.
      </div>

      {/* Projects */}
      <section className="mb-14">
        <div className="flex items-end justify-between mb-5">
          <div>
            <div className="label">Active projects ({active_project_count})</div>
          </div>
          <button
            onClick={() => setShowCreateProject(true)}
            className="btn-copper text-sm flex items-center gap-2"
            data-testid="add-project-button"
          >
            <Plus size={14} /> New project
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {activeProjects.map((p) => (
            <ProjectSlot key={p.id} project={p} />
          ))}
          <ProjectSlot onAdd={() => setShowCreateProject(true)} />
        </div>
      </section>

      {archived.length > 0 && (
        <section>
          <div className="label mb-5">Archived / historic projects ({archived.length})</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {archived.map((p) => (
              <Link key={p.id} to={`/admin/projects/${p.id}`} className="card-surface hover-lift p-5 block opacity-80" data-testid={`archived-project-${p.id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="font-mono text-[11px] text-[color:var(--atech-text-muted)]">{p.project_reference}</div>
                  <StatusPill status={p.current_status} />
                </div>
                <div className="font-display text-lg text-[color:var(--atech-text)] leading-tight">{p.project_name}</div>
                <div className="text-xs text-[color:var(--atech-text-muted)] mt-2">Updated {formatDate(p.updated_at)}</div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {showEditClient && (
        <EditClientModal
          client={client}
          onClose={() => setShowEditClient(false)}
          onSaved={() => { setShowEditClient(false); load(); }}
        />
      )}
      {showCreateProject && (
        <CreateProjectModal
          clients={[client]}
          onClose={() => setShowCreateProject(false)}
          onCreated={() => { setShowCreateProject(false); load(); }}
        />
      )}
    </div>
  );
}

function ProjectSlot({ project, onAdd }) {
  if (!project) {
    return (
      <button
        onClick={onAdd}
        className="border border-dashed border-[color:var(--atech-border)] rounded-[2px] p-6 text-left min-h-[148px] flex flex-col items-start justify-center transition-colors hover:border-[color:var(--atech-copper)] hover:text-[color:var(--atech-copper)] text-[color:var(--atech-text-muted)] cursor-pointer"
        data-testid="project-slot-add"
      >
        <FolderOpen size={18} />
        <div className="mt-3 text-sm">New project</div>
        <div className="text-[10px] uppercase tracking-[0.18em] mt-1">
          Create a project for this client
        </div>
      </button>
    );
  }
  return (
    <Link to={`/admin/projects/${project.id}`} className="card-surface hover-lift p-5 block" data-testid={`project-slot-${project.id}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="font-mono text-[11px] text-[color:var(--atech-text-muted)]">{project.project_reference}</div>
        <StatusPill status={project.current_status} />
      </div>
      <div className="font-display text-lg text-[color:var(--atech-text)] leading-tight mb-2 line-clamp-2">
        {project.project_name}
      </div>
      <div className="text-xs text-[color:var(--atech-text-dim)] truncate">{project.project_type}</div>
      <div className="text-[10px] uppercase tracking-[0.16em] text-[color:var(--atech-text-muted)] mt-3">
        Updated {formatDate(project.updated_at)}
      </div>
    </Link>
  );
}

const inputCls = "w-full bg-[color:var(--atech-surface-elevated)] border border-[color:var(--atech-border)] rounded-[2px] px-3 py-2.5 text-sm text-[color:var(--atech-text)] focus:outline-none focus:border-[color:var(--atech-copper)]";

function EditClientModal({ client, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: client.name || "",
    contact_name: client.contact_name || "",
    contact_email: client.contact_email || "",
    contact_phone: client.contact_phone || "",
    notes: client.notes || "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try { await api.patch(`/clients/${client.id}`, form); onSaved(); }
    catch (e2) { setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message); }
    finally { setBusy(false); }
  };
  return (
    <Modal onClose={onClose} title="Edit client">
      <form onSubmit={submit} className="space-y-4" data-testid="edit-client-form">
        <div><span className="label block mb-2">Name</span><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} /></div>
        <div><span className="label block mb-2">Contact name</span><input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} className={inputCls} /></div>
        <div><span className="label block mb-2">Contact email</span><input type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} className={inputCls} /></div>
        <div><span className="label block mb-2">Contact phone</span><input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} className={inputCls} /></div>
        <div><span className="label block mb-2">Notes</span><textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={`${inputCls} resize-none`} /></div>
        {err && <div className="text-xs text-[#F87171]">{err}</div>}
        <div className="flex justify-end gap-3">
          <button type="button" onClick={onClose} className="text-sm text-[color:var(--atech-text-dim)]">Cancel</button>
          <button type="submit" disabled={busy} className="btn-copper text-sm" data-testid="edit-client-submit">{busy ? "Saving…" : "Save"}</button>
        </div>
      </form>
    </Modal>
  );
}

function Modal({ title, children, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="card-surface w-full max-w-md p-8 fade-up" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-display text-xl text-[color:var(--atech-text)]">{title}</h3>
          <button onClick={onClose} className="text-[color:var(--atech-text-dim)] hover:text-[color:var(--atech-text)]">✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}
